import Vue from "vue";
import axios from "axios";
import * as oidc from "oidc-client-ts";
import { Pinia } from "pinia";
import { useUserStore } from "@/plugins/pinia";
import { UserType } from "@/features/Authentication/PlatformUserInfoService";

export interface Session {
    id: string;
    name: string;
    type: UserType;
    email: string;
}

export class SessionService {
    private userManager: oidc.UserManager;
    private userStore: ReturnType<typeof useUserStore>;

    private sessionsURL = "/api/v1/sessions";
    private userManagerSettings: oidc.UserManagerSettings = {
        authority: Vue.prototype.$configuration.oidcLogin.authority,
        client_id: Vue.prototype.$configuration.oidcLogin.clientId,
        redirect_uri: Vue.prototype.$configuration.oidcLogin.loginRedirectUri,
        silent_redirect_uri: Vue.prototype.$configuration.oidcLogin.silentRedirectUri,
        post_logout_redirect_uri: Vue.prototype.$configuration.oidcLogin.logoutRedirectUri,
        userStore: new oidc.WebStorageStateStore({
            store: window.localStorage,
        }),
        scope: "cqms openid profile platform",
        monitorSession: true,
        automaticSilentRenew: true,
    };

    constructor(pinia: Pinia) {
        // We can at least set up the user manager
        this.userManager = new oidc.UserManager(this.userManagerSettings);
        this.userStore = useUserStore(pinia);
        // Although establishing the session state for the app is async,
        // and can't be done here. See "tryInitializeUserSession"
    }

    /**
     * A separate, async method to establish internal state
     * since constructors can't be async
     */
    async tryInitializeUserSession() {
        console.log("Trying to initialize user's session information");
        {
            // Option 1: We are returning from a login redirect
            // Doing this first because it would override anything in the user manager
            // SILENT login redirects go to auth-silent-callback.html instead of here
            console.log("Checking to see if this is a return from a login flow");
            try {
                const user = await this.userManager.signinRedirectCallback();
                console.log("Successful return from login flow. Moving to step 2.");
                await this.getUserDataFromServer();
                // Redirect back to the page that the user was on
                window.location.href = (user.state as string) || "/";
                return;
            } catch {
                console.log("Not a return from a login flow");
                // Continue to next option
            }
        }
        {
            // Option 2: We might be returning from a signout. This overrides any default behavior as well
            // Nothing to do, because unauthenticated users are redirected to signin page
        }
        {
            // Option 3: User info is already in the local storage cache
            console.log("Checking to see if user is stored in the cache");
            const user = await this.userManager.getUser();
            if (user !== null) {
                console.log("User is stored in the cache. Moving to step 2");

                if (user.expired) {
                    console.log("User is expired, trying silent sign in");
                    try {
                        await this.userManager.signinSilent();
                    } catch {
                        console.log("Silent sign in failed");
                    }
                }

                await this.getUserDataFromServer();
                // All good! no need to do anything else
                return;
            } else {
                console.log("No user info stored in the cache");
            }
        }
        console.log("Could not establish a user session.");
    }

    /**
     * Login step 2: Get additional info from the server
     */
    private async getUserDataFromServer() {
        try {
            // A header needs to be stuffed here manually rather than Axios Interceptors
            // because the interceptor should only work if the user session is fully established
            const authToken = await this.getToken();
            const userData = await axios.get(this.sessionsURL, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });

            const session = userData.data as Session;

            // Now a session is fully established
            // We can store it for the rest of the app to use
            this.userStore.storeUser(session);
        } catch (e: any) {
            console.log("Error retrieving session");
            console.log(e);
            if (e.response && e.response.status == 401) {
                console.log("Got a 401");
                // Got a 401 from getting session data. Try to sign in silently to get it back.
                try {
                    const user = await this.userManager.signinSilent();

                    if (user != null) {
                        console.log("Retrieved a user from silent signin");
                        this.userManager.storeUser(user);
                    } else {
                        console.log("No user retrieved from silent signin- must log in again");
                        // User is logged out from identity provider as well
                        // and will have to manually sign in again.
                        this.login();
                    }
                } catch (e) {
                    console.log("Failed to sign in silently");
                    console.log(e);
                }
            }
        }
    }

    login(): Promise<void> {
        return this.userManager.signinRedirect({
            state: this.userStore.getPostLoginRoute(),
        });
    }

    logout(): Promise<void> {
        // Clear out all our local data before triggering OIDC logout flow
        this.userStore.clearUser();

        return this.userManager.signoutRedirect();
    }

    async getToken(): Promise<string> {
        const user = await this.userManager.getUser();
        if (user === null) {
            throw new Error("User is not logged in");
        }
        return user.access_token;
    }

    async isUserLoggedIn() {
        return (await this.userManager.getUser()) !== null;
    }

    saveRouteForPostLoginRedirect(route: string) {
        // Store the route in Pinia to be sent as the 'state' parameter when signing in.
        // On successful session initialization, we'll read the state parameter and redirect back to this route.
        this.userStore.storePostLoginRoute(route);
    }
}
